import { useEffect, useRef, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { loadXlsx } from '../../shared/utils/loadXlsx'
import {
  getBrands,
  createBrand,
  updateBrand,
  deleteBrand,
  importBrands,
} from '../../shared/api/brands.api'
import type { Brand } from '../../shared/api/brands.api'
import { PageLoader } from '../../shared/ui/Spinner'
import { toast } from '../../shared/ui/toastStore'
import { useAuthStore } from '../../modules/auth/authStore'
import '../../shared/ui/ag-grid-theme.css'

// Текст ошибки API для тоста; сетевой сбой без ответа сервера — русский запасной
// текст (axios отдал бы английское «Network Error»).
function apiErrorText(err: unknown, fallback: string): string {
  const msg = (err as { response?: { data?: { error?: { message?: string } } } }).response?.data
    ?.error?.message
  return msg ?? fallback
}

// ─── Стиль поля в строке ────────────────────────────────────────────────────

const cellInput =
  'w-full px-2 py-1 border border-transparent hover:border-gray-200 rounded-lg text-xs focus:outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500 bg-transparent transition-colors'

// Роли, которым сервер разрешает запись брендов (brands.routes.ts). РОП и
// Генеральный директор — решение Сергея 14.09.2026; остальные видят справочник
// только для чтения.
const BRAND_WRITE_ROLES = new Set(['ADMIN', 'GENERAL_DIRECTOR', 'MANAGER', 'ROP', 'ASSISTANT'])

// ─── Форма добавления новой строки ──────────────────────────────────────────

function AddBrandRow({
  onAdd,
}: {
  onAdd: (data: { factory: string; productType: string; brand: string; website: string }) => void
}) {
  const [factory, setFactory] = useState('')
  const [productType, setProductType] = useState('')
  const [website, setWebsite] = useState('')

  const handleSave = () => {
    if (!factory.trim()) return
    onAdd({ factory, productType, brand: '', website })
    setFactory('')
    setProductType('')
    setWebsite('')
  }

  const handleKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSave()
  }

  return (
    <tr className="bg-blue-50/40">
      <td className="px-3 py-2">
        <input
          type="text"
          value={factory}
          onChange={(e) => setFactory(e.target.value)}
          onKeyDown={handleKey}
          placeholder="Наименование завода"
          className="w-full px-2 py-1 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-brand-500"
        />
      </td>
      <td className="px-3 py-2">
        <input
          type="text"
          value={productType}
          onChange={(e) => setProductType(e.target.value)}
          onKeyDown={handleKey}
          placeholder="Тип продукции"
          className="w-full px-2 py-1 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-brand-500"
        />
      </td>
      <td className="px-3 py-2">
        <input
          type="text"
          value={website}
          onChange={(e) => setWebsite(e.target.value)}
          onKeyDown={handleKey}
          placeholder="https://..."
          className="w-full px-2 py-1 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-brand-500"
        />
      </td>
      <td className="px-3 py-2 text-center">
        <button
          onClick={handleSave}
          disabled={!factory.trim()}
          className="px-3 py-1 bg-brand-600 text-white text-xs rounded-lg hover:bg-brand-700 disabled:opacity-40 transition-colors"
        >
          Добавить
        </button>
      </td>
    </tr>
  )
}

// ─── Строка существующего бренда с inline-редактированием ───────────────────

function BrandRow({
  brand: initial,
  onUpdate,
  onDelete,
  deleting,
  canEdit,
}: {
  brand: Brand
  onUpdate: (
    id: string,
    data: { factory: string; productType: string; brand: string; website: string },
  ) => void
  onDelete: (id: string) => void
  deleting: boolean
  canEdit: boolean
}) {
  const [factory, setFactory] = useState(initial.factory)
  const [productType, setProductType] = useState(initial.productType ?? '')
  const [website, setWebsite] = useState(initial.website ?? '')

  // Синхронизируем локальный стейт при обновлении данных с сервера
  useEffect(() => {
    setFactory(initial.factory)
    setProductType(initial.productType ?? '')
    setWebsite(initial.website ?? '')
  }, [initial])

  const isDirty =
    factory !== initial.factory ||
    productType !== (initial.productType ?? '') ||
    website !== (initial.website ?? '')

  const save = () => {
    if (!isDirty) return
    onUpdate(initial.id, { factory, productType, brand: initial.brand, website })
  }

  const handleKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      const el = e.target as HTMLElement
      el.blur()
    }
  }

  // Без права записи — просто текст: ни полей правки, ни кнопки удаления.
  if (!canEdit) {
    return (
      <tr>
        <td className="px-3 py-2 text-xs text-gray-900">{initial.factory}</td>
        <td className="px-3 py-2 text-xs text-gray-700">{initial.productType || '—'}</td>
        <td className="px-3 py-2 text-xs text-gray-700" title={initial.website || undefined}>
          {initial.website || '—'}
        </td>
        <td className="px-3 py-2" />
      </tr>
    )
  }

  return (
    <tr>
      <td className="px-3 py-2">
        <input
          value={factory}
          onChange={(e) => setFactory(e.target.value)}
          onBlur={save}
          onKeyDown={handleKey}
          className={cellInput}
        />
      </td>
      <td className="px-3 py-2">
        <input
          value={productType}
          onChange={(e) => setProductType(e.target.value)}
          onBlur={save}
          onKeyDown={handleKey}
          placeholder="—"
          className={cellInput}
        />
      </td>
      <td className="px-3 py-2">
        {/* Сайт: в режиме редактирования — input, в покое — ссылка.
            title даёт нативный tooltip с полным URL — длинные ссылки
            обрезаются справа без многоточия (аудит 23.04). */}
        <input
          value={website}
          onChange={(e) => setWebsite(e.target.value)}
          onBlur={save}
          onKeyDown={handleKey}
          placeholder="https://..."
          title={website || undefined}
          className={cellInput}
        />
      </td>
      <td className="px-3 py-2 text-center">
        <button
          onClick={() => onDelete(initial.id)}
          disabled={deleting}
          className="text-gray-300 hover:text-red-500 transition-colors disabled:opacity-40 text-base"
          title="Удалить"
        >
          🗑
        </button>
      </td>
    </tr>
  )
}

// ─── Главная страница ─────────────────────────────────────────────────────────

export function BrandsPage() {
  const queryClient = useQueryClient()
  const { user } = useAuthStore()
  const isAdmin = user?.role === 'ADMIN'
  const canEdit = BRAND_WRITE_ROLES.has(user?.role ?? '')
  const [search, setSearch] = useState('')
  const [importResult, setImportResult] = useState<{ inserted: number; skipped: number } | null>(
    null,
  )
  const [importing, setImporting] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const { data: brands = [], isLoading } = useQuery({
    queryKey: ['brands'],
    queryFn: getBrands,
  })

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ['brands'] })

  const createMutation = useMutation({
    mutationFn: (data: { factory: string; productType: string; brand: string; website: string }) =>
      createBrand({
        factory: data.factory,
        productType: data.productType || null,
        brand: data.brand,
        website: data.website || null,
      }),
    onSuccess: invalidate,
    onError: (err) => toast.error(apiErrorText(err, 'Не удалось добавить бренд')),
  })

  const updateMutation = useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string
      data: { factory: string; productType: string; brand: string; website: string }
    }) =>
      updateBrand(id, {
        factory: data.factory,
        productType: data.productType || null,
        brand: data.brand,
        website: data.website || null,
      }),
    onSuccess: invalidate,
    onError: (err) => toast.error(apiErrorText(err, 'Не удалось сохранить бренд')),
  })

  const deleteMutation = useMutation({
    mutationFn: deleteBrand,
    onSuccess: invalidate,
    onError: (err) => toast.error(apiErrorText(err, 'Не удалось удалить бренд')),
  })

  // ── Экспорт в Excel ──────────────────────────────────────────────────────
  const handleExport = async () => {
    try {
      const XLSX = await loadXlsx()
      const ws = XLSX.utils.json_to_sheet(
        brands.map((b) => ({
          'Наименование завода': b.factory,
          'Тип продукции': b.productType ?? '',
          Сайт: b.website ?? '',
        })),
      )
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, 'Бренды')
      XLSX.writeFile(wb, 'brands.xlsx')
    } catch (err) {
      // Загрузка чанка xlsx может не дойти (старая вкладка после деплоя) —
      // без catch кнопка молча ничего не делала бы.
      toast.error(apiErrorText(err, 'Не удалось выгрузить бренды в Excel'))
    }
  }

  // ── Импорт из Excel ──────────────────────────────────────────────────────
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    e.target.value = ''
    setImporting(true)
    setImportResult(null)
    // Чанк xlsx тянем отдельным try: у его ошибки уже есть свой русский текст
    // («обновите страницу»), а общий catch подменил бы его советом «проверьте
    // формат Excel» — для не доехавшего чанка совет неверный.
    let XLSX: Awaited<ReturnType<typeof loadXlsx>>
    try {
      XLSX = await loadXlsx()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Не удалось загрузить модуль Excel')
      setImporting(false)
      return
    }
    try {
      const ab = await file.arrayBuffer()
      const wb = XLSX.read(ab, { type: 'array' })
      const ws = wb.Sheets[wb.SheetNames[0]]
      const raw = XLSX.utils.sheet_to_json<Record<string, unknown>>(ws)
      const rows = raw
        .map((r) => ({
          factory: String(r['Наименование завода'] ?? '').trim(),
          productType: r['Тип продукции'] ? String(r['Тип продукции']).trim() : null,
          website: r['Сайт'] ? String(r['Сайт']).trim() : null,
        }))
        .filter((r) => r.factory)
      const result = await importBrands(rows)
      setImportResult(result)
      if (result.inserted > 0) invalidate()
    } catch (err) {
      // Раньше catch не было: кривой файл или 422 сервера молча пропадали —
      // администратор видел только, что зелёная плашка не появилась.
      toast.error(apiErrorText(err, 'Не удалось загрузить файл брендов — проверьте формат Excel'))
    } finally {
      setImporting(false)
    }
  }
  // ─────────────────────────────────────────────────────────────────────────

  // Фильтрация по поиску (по всем полям)
  const filtered = brands.filter((b) => {
    if (!search.trim()) return true
    const q = search.toLowerCase()
    return (
      b.factory.toLowerCase().includes(q) ||
      (b.productType ?? '').toLowerCase().includes(q) ||
      b.brand.toLowerCase().includes(q) ||
      (b.website ?? '').toLowerCase().includes(q)
    )
  })

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h1 className="text-xl font-semibold text-gray-900">Бренды</h1>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm text-gray-400">{brands.length} записей</span>
          {isAdmin && (
            <>
              <button
                onClick={() => void handleExport()}
                disabled={brands.length === 0}
                className="px-3 py-1.5 border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-50 disabled:opacity-40 transition-colors"
              >
                ↓ Скачать Excel
              </button>
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={importing}
                className="px-3 py-1.5 bg-brand-600 text-white text-sm rounded-lg hover:bg-brand-700 disabled:opacity-50 transition-colors"
              >
                {importing ? 'Загрузка...' : '↑ Загрузить Excel'}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                className="hidden"
                onChange={handleFileChange}
              />
            </>
          )}
        </div>
      </div>

      {importResult && (
        <div className="px-3 py-2 rounded-lg text-sm bg-green-50 border border-green-200 text-green-800">
          Импорт завершён: добавлено <strong>{importResult.inserted}</strong>, пропущено дублей{' '}
          <strong>{importResult.skipped}</strong>
          <button
            onClick={() => setImportResult(null)}
            className="ml-3 text-green-600 hover:text-green-800 font-medium"
          >
            ×
          </button>
        </div>
      )}

      {/* Поиск */}
      <input
        type="text"
        placeholder="Поиск по заводу, бренду, типу..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 w-72"
      />

      {isLoading ? (
        <PageLoader />
      ) : (
        <div className="table-base overflow-x-auto">
          <table className="text-sm">
            <thead>
              <tr>
                <th className="px-3 py-3 w-1/3">Наименование завода</th>
                <th className="px-3 py-3 w-1/3">Тип продукции</th>
                <th className="px-3 py-3 w-1/3">Сайт</th>
                <th className="px-3 py-3 w-16 text-center">—</th>
              </tr>
            </thead>
            <tbody>
              {/* Строка добавления первая — только ролям с правом записи */}
              {canEdit && <AddBrandRow onAdd={(d) => createMutation.mutate(d)} />}

              {filtered.length === 0 && !isLoading ? (
                <tr>
                  <td colSpan={4} className="px-4 py-6 text-center text-gray-400 text-sm">
                    {search
                      ? 'Ничего не найдено'
                      : canEdit
                        ? 'Нет записей — добавьте первую выше'
                        : 'Нет записей'}
                  </td>
                </tr>
              ) : (
                filtered.map((b) => (
                  <BrandRow
                    key={b.id}
                    brand={b}
                    onUpdate={(id, data) => updateMutation.mutate({ id, data })}
                    onDelete={(id) => deleteMutation.mutate(id)}
                    deleting={deleteMutation.isPending}
                    canEdit={canEdit}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
